const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com'

export interface GalleryItem {
  id: string
  src: string
  width: number
  height: number
  title: string
  description: string
  mediaType: 'image' | 'video'
  tags: {
    events: string[]
    years: number[]
    members: string[]
    locations: string[]
    scheduledEvents: string[]
  }
  uploadedBy: string
  uploadedAt: string
  thumbnailUrl?: string
}

export interface CreateGalleryItemData {
  title: string
  description?: string
  mediaUrl: string
  thumbnailUrl?: string
  mediaType?: 'image' | 'video'
  width?: number
  height?: number
  tags?: {
    events?: string[]
    years?: number[]
    members?: string[]
    locations?: string[]
    scheduledEvents?: string[]
  }
}

export interface UpdateGalleryItemData {
  title?: string
  description?: string
  mediaUrl?: string
  thumbnailUrl?: string
  width?: number
  height?: number
  tags?: {
    events?: string[]
    years?: number[]
    members?: string[]
    locations?: string[]
    scheduledEvents?: string[]
  }
}

export interface UploadUrlResponse {
  uploadUrl: string
  mediaUrl: string
  key: string
}

export interface GalleryResponse {
  items: GalleryItem[]
  count: number
  hasMore: boolean
  nextKey?: string
}

export interface GallerySearchParams {
  limit?: number
  lastKey?: string
  search?: string
  events?: string[]
  locations?: string[]
  members?: string[]
  years?: number[]
  sortBy?: string
}

export const galleryApi = {
  // Get gallery items with pagination and search
  async getItems(
    getAccessTokenSilently: () => Promise<string>, 
    options?: GallerySearchParams
  ): Promise<GalleryResponse> {
    const token = await getAccessTokenSilently()
    
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.lastKey) params.append('lastKey', options.lastKey)
    if (options?.search) params.append('search', options.search)
    if (options?.events && options.events.length > 0) {
      params.append('events', options.events.join(','))
    }
    if (options?.locations && options.locations.length > 0) {
      params.append('locations', options.locations.join(','))
    }
    if (options?.members && options.members.length > 0) {
      params.append('members', options.members.join(','))
    }
    if (options?.years && options.years.length > 0) {
      params.append('years', options.years.join(','))
    }
    // Temporarily removed sortBy parameter
    // if (options?.sortBy) {
    //   params.append('sortBy', options.sortBy)
    // }
    
    const url = `${API_BASE_URL}/v1/gallery${params.toString() ? `?${params}` : ''}`
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch gallery items: ${response.status}`)
    }

    return response.json()
  },

  // Get a single gallery item
  async getItem(id: string, getAccessTokenSilently: () => Promise<string>): Promise<GalleryItem> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/gallery/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch gallery item: ${response.status}`)
    }

    return response.json()
  },

  // Create a new gallery item
  async createItem(data: CreateGalleryItemData, getAccessTokenSilently: () => Promise<string>): Promise<{ id: string, message: string }> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/gallery`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(`Failed to create gallery item: ${response.status}`)
    }

    return response.json()
  },

  // Update a gallery item
  async updateItem(id: string, data: UpdateGalleryItemData, getAccessTokenSilently: () => Promise<string>): Promise<GalleryItem> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/gallery/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(`Failed to update gallery item: ${response.status}`)
    }

    const result = await response.json()
    
    // If the backend returns just a message, fetch the updated item
    if (result.message && !result.id) {
      return this.getItem(id, getAccessTokenSilently)
    }
    
    return result
  },

  // Delete a gallery item
  async deleteItem(id: string, getAccessTokenSilently: () => Promise<string>): Promise<{ message: string }> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/gallery/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to delete gallery item: ${response.status}`)
    }

    return response.json()
  },

  // Get presigned URL for file upload
  async getUploadUrl(fileName: string, fileType: string, getAccessTokenSilently: () => Promise<string>): Promise<UploadUrlResponse> {
    const token = await getAccessTokenSilently()
    
    const params = new URLSearchParams({
      action: 'upload-url',
      fileName,
      fileType
    })
    
    const response = await fetch(`${API_BASE_URL}/v1/gallery?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to get upload URL: ${response.status}`)
    }

    return response.json()
  },

  // Upload file to S3 using presigned URL
  async uploadFile(file: File, uploadUrl: string, onProgress?: (progress: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100
            onProgress(progress)
          }
        })
      }
      
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve()
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`))
        }
      })
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'))
      })
      
      xhr.open('PUT', uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  }
}
